import { handleKMZ, Mission } from "@rogoag/shared-ts";
import type { Job } from "@domain/job/types/Job";
import type { IAttachmentDownloader } from "@infra/interfaces/browser/IAttachmentDownloader";

export interface MissionsUseCaseDeps {
	downloader: IAttachmentDownloader;
}

/**
 * Load and cache executed/intended Mission data for a Job. Each attachment
 * load goes through the injected `IAttachmentDownloader` port, so the use case
 * doesn't depend on the browser `FileDownloader` implementation. Per-instance
 * `WeakMap` caches mean repeated calls for the same Job within a session
 * skip the network.
 */
export class MissionsUseCase {
	private readonly exeCache = new WeakMap<Job, Mission>();
	private readonly intCache = new WeakMap<Job, Mission>();

	constructor(private readonly deps: MissionsUseCaseDeps) {}

	async loadExe(job: Job): Promise<Mission | undefined> {
		const cached = this.exeCache.get(job);
		if (cached) return cached;
		const mission = await this.loadMission(job, "exeMission", "EXE");
		if (mission) this.exeCache.set(job, mission);
		return mission;
	}

	async loadInt(job: Job): Promise<Mission | undefined> {
		const cached = this.intCache.get(job);
		if (cached) return cached;
		const mission = await this.loadMission(job, "intMission", "INT");
		if (mission) this.intCache.set(job, mission);
		return mission;
	}

	private async loadMission(
		job: Job,
		attachmentKey: "exeMission" | "intMission",
		intOrExe: "INT" | "EXE",
	): Promise<Mission | undefined> {
		const attachments = job.attachments[attachmentKey];
		if (!attachments || attachments.length === 0) return undefined;
		const attachment = attachments[0];
		if (!attachment.url) return undefined;

		const isKmz = /\.kmz$/i.test(attachment.filename);
		let kml: string | undefined;

		if (isKmz) {
			const kmz = await this.deps.downloader.load<Buffer>(attachment, job.id, "arraybuffer");
			if (!kmz) return undefined;
			kml = await handleKMZ(kmz, attachment.filename);
		} else {
			kml = await this.deps.downloader.load<string>(attachment, job.id, "text");
		}

		if (!kml) return undefined;

		const filename = isKmz ? attachment.filename.replace(/\.kmz$/i, ".kml") : attachment.filename;
		try {
			return await Mission.load({ data: kml, filename }, {}, intOrExe);
		} catch (err) {
			console.warn(`Failed to parse ${intOrExe} mission:`, err);
			return undefined;
		}
	}
}
